import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, FileImage, Trash2, Upload, X, ExternalLink, CheckCircle2, AlertCircle, Paperclip,
} from "lucide-react";

/**
 * KycVaultModal — attach & manage ID proofs and forms for a Ward or Parent.
 *
 * Props:
 *   target: "ward" | "parent"
 *   id:     mongoId
 *   name:   display name
 *   readonly: bool  (true for parent portal)
 *   onClose: fn
 *
 * Two-step upload:
 *   1) POST /api/uploads/document  → returns { url, path, size, content_type }
 *   2) POST /api/admin/{target}s/{id}/documents  → attaches metadata
 */
export default function KycVaultModal({ target, id, name, readonly = false, onClose }) {
  const [state, setState] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [type, setType] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const basePath = target === "ward" ? "wards" : "parents";
  const adminUrl = `/admin/${basePath}/${id}/documents`;
  const parentUrl = target === "ward" ? `/parent/wards/${id}/documents` : `/parent/me/documents`;

  useEffect(() => {
    (async () => {
      try {
        const req1 = readonly ? api.get(parentUrl) : api.get(adminUrl);
        const [{ data }, cat] = await Promise.all([
          req1,
          readonly ? Promise.resolve({ data: null }) : api.get("/admin/kyc/types"),
        ]);
        setState(data);
        if (cat?.data) {
          setCatalog(cat.data);
          const options = cat.data[target]?.types || [];
          setType(options[0] || "other");
        }
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [id, target]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !type) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data: up } = await api.post("/uploads/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.post(adminUrl, {
        type,
        url: up.url,
        filename: file.name,
        size: up.size,
        content_type: up.content_type,
        note: note.trim() || null,
      });
      setState((s) => ({ ...s, documents: data.documents, completion: data.completion }));
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Document attached");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      const { data } = await api.delete(`${adminUrl}/${docId}`);
      setState((s) => ({ ...s, documents: data.documents, completion: data.completion }));
      toast.success("Document removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!state) return null;
  const options = catalog?.[target]?.types || [];
  const required = catalog?.[target]?.required || [];
  const labels = catalog?.labels || {};
  const comp = state.completion || {};
  const missingList = comp.missing || [];
  const isComplete = (comp.required_present || 0) >= (comp.required_total || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0B1120] border border-white/12 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="kyc-vault-modal"
      >
        <div className="sticky top-0 bg-[#0B1120] border-b border-white/12 px-5 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="brand-orange" size={22} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                {target === "ward" ? "Ward KYC" : "Parent KYC"}
              </div>
              <div className="font-display text-lg font-black uppercase tracking-tight">{name}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="kyc-close">
            <X size={20} />
          </button>
        </div>

        {/* Completion strip */}
        <div className={`m-5 md:m-6 p-4 border ${isComplete ? "border-green-500/50 bg-green-500/10" : "border-brand-orange/40 bg-brand-orange/10"}`}
             data-testid="kyc-completion">
          <div className="flex items-center gap-3 flex-wrap">
            {isComplete
              ? <CheckCircle2 size={18} className="text-green-400" />
              : <AlertCircle size={18} className="brand-orange" />}
            <div className="font-bold">
              KYC {comp.required_present || 0} of {comp.required_total || 0} required documents
            </div>
            {isComplete && (
              <span className="text-[10px] font-mono uppercase tracking-widest bg-green-500/20 text-green-300 px-2 py-0.5">
                Complete
              </span>
            )}
          </div>
          {!isComplete && missingList.length > 0 && (
            <div className="text-xs text-white/60 mt-2">
              Missing: {missingList.map((t) => labels[t] || t).join(", ")}
            </div>
          )}
        </div>

        {/* Upload form (admin only) */}
        {!readonly && (
          <div className="mx-5 md:mx-6 mb-6 bg-[#131A2B] border border-white/12 p-5" data-testid="kyc-upload-form">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-3">// Attach document</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                data-testid="kyc-type"
                className="bg-[#0B1120] border border-white/20 py-3 px-3 text-sm focus:outline-none focus:border-brand-orange"
              >
                {options.map((t) => {
                  const isReq = required.includes(t);
                  return (
                    <option key={t} value={t}>
                      {labels[t] || t}{isReq ? " (required)" : ""}
                    </option>
                  );
                })}
              </select>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="kyc-note"
                placeholder="Optional note (e.g. 'Front + back')"
                className="bg-transparent border border-white/20 py-3 px-3 text-sm focus:outline-none focus:border-brand-orange"
              />
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                onChange={upload}
                accept="application/pdf,image/jpeg,image/png,image/webp"
                data-testid="kyc-file"
                disabled={uploading}
                className="text-xs text-white/70 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-brand-blue file:text-white file:text-xs file:uppercase file:tracking-widest file:font-bold file:cursor-pointer"
              />
              {uploading && <span className="text-xs brand-orange animate-pulse">Uploading…</span>}
              <span className="text-[10px] text-white/40 font-mono">PDF / JPG / PNG / WEBP · max 10 MB</span>
            </div>
          </div>
        )}

        {/* Documents list */}
        <div className="mx-5 md:mx-6 mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">
            Attached documents ({state.documents.length})
          </div>
          <div className="border border-white/12 divide-y divide-white/8" data-testid="kyc-docs-list">
            {state.documents.length === 0 && (
              <div className="p-8 text-center text-white/40 text-sm">
                {readonly ? "No documents on file yet — the office will upload these." : "No documents yet. Upload the first one above."}
              </div>
            )}
            {state.documents.map((d) => {
              const isImg = (d.content_type || "").startsWith("image/");
              return (
                <div key={d.id} className="p-4 flex items-start gap-3" data-testid={`kyc-doc-${d.id}`}>
                  <div className="h-9 w-9 flex items-center justify-center shrink-0 bg-white/5">
                    {isImg ? <FileImage size={16} className="brand-blue" /> : <FileText size={16} className="brand-orange" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-bold text-sm truncate">{d.label || d.type}</div>
                      {required.includes(d.type) && (
                        <span className="text-[9px] font-mono uppercase tracking-widest bg-brand-orange/20 brand-orange px-1.5 py-0.5">
                          required
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/50 mt-0.5 truncate">
                      {d.filename || "(unnamed)"}{d.size ? ` · ${Math.round(d.size / 1024)} KB` : ""}
                    </div>
                    {d.note && (
                      <div className="text-[11px] text-white/60 mt-1 italic">“{d.note}”</div>
                    )}
                    <div className="text-[10px] text-white/40 mt-1 font-mono">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                      {d.uploaded_by ? ` · by ${d.uploaded_by}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`kyc-view-${d.id}`}
                      className="text-[11px] font-mono uppercase tracking-widest border border-white/20 px-2 py-1 hover:bg-white/10 flex items-center gap-1"
                    >
                      <Paperclip size={11} /> View <ExternalLink size={9} />
                    </a>
                    {!readonly && (
                      <button
                        onClick={() => remove(d.id)}
                        data-testid={`kyc-delete-${d.id}`}
                        className="text-[11px] font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-2 py-1 hover:bg-red-500 hover:text-white flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
